import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getLanguageData } from '../data/languages';
import { LanguageCode, WordData } from '../types/types';
import { useTheme } from './ThemeContext';
import { ArrowLeft, RotateCw, Volume2, Target, Rocket, Heart, Home, Check } from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import SummaryScreen from './SummaryScreen';
import { useUserProgress, useAudio } from '../hooks/hooks';
import { getWordTranslation } from '../utils/localizedContent';
import './ExerciseMode.css';
import TribalBorder from './TribalBorder';

// ===============================================
// INTERFACES ET TYPES
// ===============================================

interface ExerciseModeProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onExerciseComplete: () => void;
  checkDailyLimit?: (limitType: 'dailyRevisions' | 'exercisesPerDay') => boolean;
  isMobileView?: boolean;
  onShare?: () => void;
}

type ExerciseType = 'translation' | 'multipleChoice' | 'association';
type ExerciseStatus = 'dashboard' | 'countdown' | 'loading' | 'exercise' | 'summary';

interface Exercise {
  type: ExerciseType;
  question: string;
  options?: string[];
  correctAnswer: string | string[];
  category: string;
  subcategory?: string;
  wordKey?: string;
  pairs?: Array<{
    left: string;
    right: string;
    id: string;
  }>;
  data?: {
    translation?: string;
    audio?: string;
  };
}

interface UserStats {
  totalExercises: number;
  correctAnswers: number;
  currentStreak: number;
  bestStreak: number;
  averageScore: number;
  exercisesToday: number;
  lastSessionDate: string;
}

interface SessionResult {
  correct: number;
  total: number;
  exercises: Exercise[];
  userAnswers: string[];
  isCorrect: boolean[];
  sessionId: string;
  duration: number;
  score: number;
}

// ===============================================
// FONCTIONS UTILITAIRES
// ===============================================

const shuffleArray = <T extends any>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const cleanParentheses = (text: string): string => {
  return text.replace(/\s*\([^)]*\)/g, '');
};

const normalizeText = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[.,?!;:'"\-_()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const FRENCH_DETERMINANTS = /^(le |la |les |l'|l |un |une |des |du |d'|de la |de l'|de |au |aux )/i;
// Marqueurs de classe nominale wolof (suffixes séparés par un espace)
const WOLOF_DETERMINANTS = / (bi|ba|bu|gi|ga|gu|ji|ja|ju|ki|ka|ku|wi|wa|wu|yi|ya|yu|li|la|lu|mi|ma|mu|si|sa|su|ci|di|fi|ni|o|a|i)$/i;

const stripDeterminants = (text: string): string => {
  let result = text;
  let prev = '';
  while (result !== prev) {
    prev = result;
    result = result.replace(FRENCH_DETERMINANTS, '');
    result = result.replace(WOLOF_DETERMINANTS, '');
  }
  return result.trim();
};

const compareWithTolerance = (userAnswer: string, correctAnswer: string): boolean => {
  const normalizedUser = normalizeText(userAnswer);
  const normalizedCorrect = normalizeText(correctAnswer);

  if (normalizedUser === normalizedCorrect) {
    return true;
  }

  // Tolérance pour les déterminants français et marqueurs de classe wolof
  const strippedUser = stripDeterminants(normalizedUser);
  const strippedCorrect = stripDeterminants(normalizedCorrect);

  if (strippedUser === strippedCorrect) {
    return true;
  }

  const maxDistance = normalizedCorrect.length <= 5 ? 1 : 2;
  const distance = levenshteinDistance(normalizedUser, normalizedCorrect);
  if (distance <= maxDistance) return true;

  // Aussi comparer les versions sans déterminants avec tolérance Levenshtein
  const maxDistanceStripped = strippedCorrect.length <= 5 ? 1 : 2;
  const distanceStripped = levenshteinDistance(strippedUser, strippedCorrect);
  return distanceStripped <= maxDistanceStripped;
};

const levenshteinDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = a[j - 1] === b[i - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[b.length][a.length];
};

// ===============================================
// SYSTÈME DE MAPPING POUR LES CATÉGORIES
// ===============================================

/**
 * Mapping entre les noms affichés et les clés réelles des catégories
 * Permet de gérer les différences de nommage entre les langues
 */
const createCategoryMapping = (languageData: any) => {
  const realCategories = Object.keys(languageData.categories || {});
  
  // Mapping explicite pour les cas connus
  const explicitMapping: Record<string, string[]> = {
    'Salutations et expressions courantes': ['Salutations', 'Expressions', 'Salutations et expressions courantes'],
    'Nourriture et boissons': ['Nourriture', 'Boissons', 'Nourriture et boissons', 'Alimentation'],
    'Famille et relations': ['Famille', 'Relations', 'Famille et relations'],
    'Parties du corps': ['Corps', 'Parties du corps', 'Anatomie'],
    'Verbes courants': ['Verbes', 'Verbes courants', 'Actions'],
    'Nombres': ['Nombres', 'Numéros', 'Chiffres'],
    'Temps': ['Temps', 'Temporel', 'Chronologie'],
    'Couleurs': ['Couleurs', 'Colors'],
    'Animaux': ['Animaux', 'Faune'],
    'Grammaire': ['Grammaire', 'Grammar', 'Grammatical'],
    'Objets du quotidien': ['Objets du quotidien', 'Objets', 'Quotidien']
  };

  // Fonction pour trouver la correspondance
  const findMatchingCategory = (displayName: string): string | null => {
    // 1. Correspondance exacte
    if (realCategories.includes(displayName)) {
      return displayName;
    }

    // 2. Correspondance via mapping explicite
    const possibleKeys = explicitMapping[displayName] || [];
    for (const key of possibleKeys) {
      if (realCategories.includes(key)) {
        return key;
      }
    }

    // 3. Correspondance partielle (insensible à la casse)
    const normalizedDisplay = displayName.toLowerCase().replace(/[^a-z]/g, '');
    for (const realKey of realCategories) {
      const normalizedReal = realKey.toLowerCase().replace(/[^a-z]/g, '');
      if (normalizedDisplay.includes(normalizedReal) || normalizedReal.includes(normalizedDisplay)) {
        return realKey;
      }
    }

    return null;
  };

  return { findMatchingCategory, realCategories };
};

// ===============================================
// FONCTION D'EXTRACTION DES MOTS (GÈRE LES SOUS-CATÉGORIES)
// ===============================================

// cleanedTranslation est localisé selon `language` (voir getWordTranslation) —
// les questions générées plus bas ('exercise.questions.whatMeansInFrench' etc.)
// nomment désormais explicitement la langue d'interface active (voir la
// correction des clés en/translation.json), donc affichage et validation de
// réponse restent cohérents quelle que soit la langue.
const extractWordsFromCategory = (categoryData: any, language: string): Array<{
  word: string;
  originalWord: string;
  data: WordData;
  cleanedTranslation: string;
}> => {
  const extractedWords: Array<{
    word: string;
    originalWord: string;
    data: WordData;
    cleanedTranslation: string;
  }> = [];

  const processEntries = (entries: any, parentKey = '') => {
    Object.entries(entries).forEach(([key, value]) => {
      // Si c'est un mot avec une traduction directe
      if (value &&
          typeof value === 'object' &&
          (value as any).translation &&
          !key.includes('_')) {

        const cleanedWord = cleanParentheses(key);
        const cleanedTranslation = cleanParentheses(getWordTranslation(value as WordData, language) || '');

        if (cleanedTranslation.length > 0) {
          extractedWords.push({
            word: cleanedWord,
            originalWord: key,
            data: value as WordData,
            cleanedTranslation
          });
        }
      }
      // Si c'est une sous-catégorie (comme "Pronoms personnels", "Prépositions", etc.)
      else if (value && 
               typeof value === 'object' && 
               !(value as any).translation &&
               !key.includes('_')) {
        
processEntries(value, key);
      }
    });
  };

  processEntries(categoryData);
  
if (extractedWords.length > 0) {
  }
  
  return extractedWords;
};

const generateFallbackExercises = (languageData: any, languageInfo: any, t: (key: string, opts?: any) => string, language: string): Exercise[] => {
const fallbackExercises: Exercise[] = [];
  const allCategories = Object.keys(languageData.categories || {});

  // Prendre les premières catégories disponibles
  const availableCategories = allCategories.slice(0, 5);

  availableCategories.forEach(categoryKey => {
    const categoryWords = languageData.categories[categoryKey];
    if (!categoryWords) return;

    // Utiliser la nouvelle fonction d'extraction
    const validWords = extractWordsFromCategory(categoryWords, language).slice(0, 3);

    validWords.forEach(({ word,  cleanedTranslation, originalWord }) => {
      if (cleanedTranslation) {
        // Exercice de traduction simple
        fallbackExercises.push({
          type: 'translation',
          question: t('exercise.questions.translateInto', { word: cleanedTranslation, lang: languageInfo.name }),
          correctAnswer: word,
          category: categoryKey,
          wordKey: originalWord
        });
      }
    });
  });

return fallbackExercises;
};

// ===============================================
// FONCTION DE DÉBOGAGE POUR LES CATÉGORIES
// ===============================================

const debugCategoryStructure = (languageData: any) => {
if (languageData?.categories) {
    Object.keys(languageData.categories).forEach(key => {
      const category = languageData.categories[key];
      
      // Compter les mots en utilisant la nouvelle fonction d'extraction
      // (debug uniquement — la langue n'a pas d'incidence sur le comptage)
      const extractedWords = extractWordsFromCategory(category, 'fr');
      const wordCount = extractedWords.length;
      
// Afficher quelques exemples de mots
      if (wordCount > 0) {
        const sampleWords = extractedWords.slice(0, 3).map(w => w.word);
      } else {
        // Afficher la structure interne si pas de mots directs
        const subKeys = Object.keys(category || {}).slice(0, 3);
        if (subKeys.length > 0) {
        }
      }
    });
  } else {
}
};

// ===============================================
// COMPOSANT PRINCIPAL
// ===============================================

const ExerciseMode: React.FC<ExerciseModeProps> = ({
  languageCode,
  onBackToCategories,
  onExerciseComplete,
  checkDailyLimit,
  onShare,
}) => {
  // ===============================================
  // HOOKS ET VARIABLES
  // ===============================================


    useEffect(() => {
      return () => {};
    }, []);


  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const languageInfo = getLanguageData(languageCode);

  const playAudio = useAudio();

  // ===============================================
  // ÉTATS PRINCIPAUX - AVEC INITIALISATION DYNAMIQUE
  // ===============================================

  // Fonction pour obtenir l'état initial depuis localStorage
const getInitialExerciseState = () => {
  // ❌ DÉSACTIVÉ - Restauration localStorage
  // Raison : Causait des états incohérents où :
  // - exerciseStatus = 'exercise' 
  // - exercises = [] (vide)
  // Ce qui empêchait le composant de s'afficher
  
  /* ANCIEN CODE (NE PLUS UTILISER) :
  try {
    const savedState = localStorage.getItem(`exerciseMode_${languageCode}_state`);
    if (savedState) {
      const state = JSON.parse(savedState);
      if (state.status === 'exercise' && state.exercises?.length > 0) {
        return {
          exerciseStatus: state.status,
          countdown: state.countdown || 0,
          exercises: state.exercises || [],
          currentExercise: state.exercises[state.exerciseIndex || 0] || null,
          exerciseIndex: state.exerciseIndex || 0,
          sessionUserAnswers: state.sessionUserAnswers || [],
          sessionCorrectAnswers: state.sessionCorrectAnswers || [],
          score: state.score || 0,
          streak: state.streak || 0,
          sessionStartTime: state.sessionStartTime || Date.now()
        };
      }
    }
  } catch (error) {
}
  */
  
  // 🧹 Nettoyer tout état sauvegardé obsolète
  try {
    localStorage.removeItem(`exerciseMode_${languageCode}_state`);
} catch (e) {
}
  
  // ✅ TOUJOURS retourner un état propre et cohérent
 return {
  exerciseStatus: 'countdown' as ExerciseStatus,
  countdown: 0,
  exercises: [] as Exercise[], // ✅
  currentExercise: null as Exercise | null, // ✅
  exerciseIndex: 0,
  sessionUserAnswers: [] as string[], // ✅
  sessionCorrectAnswers: [] as boolean[], // ✅
  score: 0,
  streak: 0,
  sessionStartTime: 0
};
};

  // ✅ Appelé UNE SEULE FOIS grâce à useMemo
const [exerciseStatus, setExerciseStatus] = useState<ExerciseStatus>('countdown');
const [countdown, setCountdown] = useState<number>(0);
const [, setIsCountdownActive] = useState<boolean>(true);

// États pour les exercices - TOUJOURS PARTIR D'UN ÉTAT VIDE
const [exercises, setExercises] = useState<Exercise[]>([]);
const [currentExercise, setCurrentExercise] = useState<Exercise | null>(null);
const [exerciseIndex, setExerciseIndex] = useState<number>(0);

const [userAnswer, setUserAnswer] = useState<string>('');
const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
const [sessionResults, setSessionResults] = useState<SessionResult | null>(null);
const [sessionUserAnswers, setSessionUserAnswers] = useState<string[]>([]);
const [sessionCorrectAnswers, setSessionCorrectAnswers] = useState<boolean[]>([]);
const [score, setScore] = useState<number>(0);
const [streak, setStreak] = useState<number>(0);
const [sessionStartTime, setSessionStartTime] = useState<number>(0);

  // États pour les statistiques
  const [userStats, setUserStats] = useState<UserStats>({
    totalExercises: 0,
    correctAnswers: 0,
    currentStreak: 0,
    bestStreak: 0,
    averageScore: 0,
    exercisesToday: 0,
    lastSessionDate: ''
  });

  // États spécifiques pour les exercices d'association
  const [selectedLeftItem, setSelectedLeftItem] = useState<string | null>(null);
  const [selectedRightItem, setSelectedRightItem] = useState<string | null>(null);
  const [completedPairs, setCompletedPairs] = useState<string[]>([]);
  const [,setPairsAssociations] = useState<Record<string, string>>({});
  const [shuffledLeftItems, setShuffledLeftItems] = useState<string[]>([]);
  const [shuffledRightItems, setShuffledRightItems] = useState<string[]>([]);

  // États pour les particules
  const [particles, setParticles] = useState<Array<{ id: number; left: number; top: number; delay: number; duration: number }>>([]);

  // ===============================================
  // FONCTION GENERATEMIEXERCISES CORRIGÉE
  // ===============================================

  const generateMixedExercises = useCallback((): Exercise[] => {
const languageData = getLanguageData(languageCode);
if (!languageData || !languageData.categories) {
return [];
    }

    // Déboguer la structure des catégories
    debugCategoryStructure(languageData);
    
    const { findMatchingCategory, realCategories } = createCategoryMapping(languageData);
const allExercises: Exercise[] = [];

    // Catégories prioritaires à essayer
    const priorityCategories = [
      'Salutations et expressions courantes',
      'Nombres',
      'Temps',
      'Couleurs',
      'Nourriture et boissons',
      'Animaux',
      'Famille et relations',
      'Parties du corps',
      'Verbes courants',
      'Grammaire',
      'Objets du quotidien'
    ];

    // Collecter toutes les catégories disponibles (prioritaires + réelles)
    const categoriesToProcess: string[] = [];
    
    // 1. Ajouter les catégories prioritaires qui ont une correspondance
    priorityCategories.forEach(category => {
      const matchedKey = findMatchingCategory(category);
      if (matchedKey) {
        categoriesToProcess.push(matchedKey);
} else {
}
    });

    // 2. Ajouter les catégories réelles non encore incluses (fallback)
    realCategories.forEach(realCategory => {
      if (!categoriesToProcess.includes(realCategory)) {
        categoriesToProcess.push(realCategory);
}
    });

// Si aucune catégorie n'est disponible, retourner des exercices de base
    if (categoriesToProcess.length === 0) {
return generateFallbackExercises(languageData, languageInfo, t, i18n.language);
    }

    // Générer des exercices pour chaque catégorie disponible
    categoriesToProcess.forEach(categoryKey => {
      const categoryWords = languageData.categories[categoryKey];
if (!categoryWords || typeof categoryWords !== 'object') {
return;
      }

      // Extraire les mots de la catégorie (gérer les sous-catégories)
      const words = extractWordsFromCategory(categoryWords, i18n.language);

if (words.length === 0) {
return;
      }

      // Sélectionner quelques mots par catégorie (plus généreux)
      const wordsToUse = Math.min(8, words.length); // Augmenté de 5 à 8
      const selectedWords = shuffleArray(words).slice(0, wordsToUse);
selectedWords.forEach(({ word, originalWord, data, cleanedTranslation }) => {
        // Exercice de traduction (Fr → Langue)
        allExercises.push({
          type: 'translation',
          question: t('exercise.questions.translateInto', { word: cleanedTranslation, lang: languageInfo.name }),
          correctAnswer: word,
          category: categoryKey,
          wordKey: originalWord
        });

        // Exercice de traduction inverse (Langue → Fr)
        allExercises.push({
          type: 'translation',
          question: t('exercise.questions.whatMeansInFrench', { word }),
          correctAnswer: cleanedTranslation,
          category: categoryKey,
          wordKey: originalWord
        });

        // Exercice de choix multiple si assez d'options
        const otherWords = words
          .filter(w => w.word !== word)
          .map(w => w.word);

        if (otherWords.length >= 3) {
          const wrongOptions = shuffleArray(otherWords).slice(0, 3);
          const options = shuffleArray([word, ...wrongOptions]);

          allExercises.push({
            type: 'multipleChoice',
            question: t('exercise.questions.whichWordFor', { word: cleanedTranslation, lang: languageInfo.name }),
            options,
            correctAnswer: word,
            category: categoryKey,
            wordKey: originalWord,
            data: {
              audio: data.audio
            }
          });
        }
      });

      // Exercice d'association pour cette catégorie si assez de mots
      if (selectedWords.length >= 3) {
        const pairCount = Math.min(4, selectedWords.length);
        const pairWords = selectedWords.slice(0, pairCount);

        const pairs = pairWords.map(({ word, cleanedTranslation }, index) => ({
          left: word,
          right: cleanedTranslation,
          id: `pair-${categoryKey}-${index}`
        }));

        allExercises.push({
          type: 'association',
          question: t('exercise.questions.matchWords', { lang: languageInfo.name }),
          correctAnswer: pairs.map(p => `${p.left}:${p.right}`).join(','),
          category: categoryKey,
          pairs: pairs
        });
      }
    });

// Si pas assez d'exercices, utiliser le fallback
    if (allExercises.length < 5) {
const fallbackExercises = generateFallbackExercises(languageData, languageInfo, t, i18n.language);
      allExercises.push(...fallbackExercises);
    }

    // Mélanger tous les exercices et en prendre 20 (ou le maximum disponible)
    const shuffledExercises = shuffleArray(allExercises);
    const targetCount = Math.min(20, Math.max(10, allExercises.length)); // Entre 10 et 20
    const finalExercises = shuffledExercises.slice(0, targetCount);


    return finalExercises;
  }, [languageCode, languageInfo.name, t, i18n.language]);

  // ===============================================
  // FONCTIONS DE GESTION DES STATISTIQUES
  // ===============================================

  const loadUserStats = useCallback(() => {
    const savedStats = localStorage.getItem(`exerciseStats_${languageCode}`);
    if (savedStats) {
      const stats = JSON.parse(savedStats);
      setUserStats(stats);
    }
  }, [languageCode]);

  const saveUserStats = useCallback((newStats: UserStats) => {
    localStorage.setItem(`exerciseStats_${languageCode}`, JSON.stringify(newStats));
    setUserStats(newStats);
  }, [languageCode]);

  const updateStats = useCallback((sessionResult: SessionResult) => {
    const today = new Date().toISOString().split('T')[0];
    const isToday = userStats.lastSessionDate === today;

    const newStats: UserStats = {
      ...userStats,
      totalExercises: userStats.totalExercises + sessionResult.total,
      correctAnswers: userStats.correctAnswers + sessionResult.correct,
      currentStreak: sessionResult.score >= 70 ? userStats.currentStreak + 1 : 0,
      bestStreak: Math.max(userStats.bestStreak, sessionResult.score >= 70 ? userStats.currentStreak + 1 : 0),
      averageScore: Math.round(((userStats.averageScore * (userStats.totalExercises / sessionResult.total)) + sessionResult.score) / ((userStats.totalExercises / sessionResult.total) + 1)),
      exercisesToday: isToday ? userStats.exercisesToday + sessionResult.total : sessionResult.total,
      lastSessionDate: today
    };

    saveUserStats(newStats);
  }, [userStats, saveUserStats]);

  // ===============================================
  // FONCTIONS DE DÉCOMPTE - SUPPRESSION DU DÉMARRAGE MANUEL
  // ===============================================

  const finishCountdownAndStartExercises = useCallback(() => {
// Génération des exercices directement sans passer par loading
    setTimeout(() => {
      try {
        const newExercises = generateMixedExercises();
if (newExercises.length === 0) {
// Nettoyer l'état sauvé avant de sortir
          localStorage.removeItem(`exerciseMode_${languageCode}_state`);
          onBackToCategories();
          alert(t('exercise.errors.cannotGenerate'));
          return;
        }
        
        const firstExercise = newExercises[0];
let leftItems: string[] = [];
        let rightItems: string[] = [];
        
        if (firstExercise?.type === 'association' && firstExercise.pairs) {
          leftItems = shuffleArray([...firstExercise.pairs].map(p => p.left));
          rightItems = shuffleArray([...firstExercise.pairs].map(p => p.right));
        }
        
        // Mise à jour de tous les états
        setExercises(newExercises);
        setSessionUserAnswers(Array(newExercises.length).fill(''));
        setSessionCorrectAnswers(Array(newExercises.length).fill(false));
        setCurrentExercise(firstExercise);
        setExerciseIndex(0);
        setUserAnswer('');
        setIsCorrect(null);
        setScore(0);
        setStreak(0);
        setSessionResults(null);
        setCompletedPairs([]);
        setPairsAssociations({});
        setSelectedLeftItem(null);
        setSelectedRightItem(null);
        setShuffledLeftItems(leftItems);
        setShuffledRightItems(rightItems);
        setSessionStartTime(Date.now());
        
setExerciseStatus('exercise');
} catch (error) {
localStorage.removeItem(`exerciseMode_${languageCode}_state`);
        onBackToCategories();
        alert(t('exercise.errors.errorPrefix', { error }));
      }
    }, 0); // ✅ Pas de délai
  }, [generateMixedExercises, onBackToCategories, languageCode, t]);

  // ===============================================
  // EFFET POUR LE DÉCOMPTE - CONDITION SIMPLIFIÉE
  // ===============================================

 useEffect(() => {
  let timer: NodeJS.Timeout;
  
 
  
  // ✅ SEULEMENT si on est en mode countdown
  if (exerciseStatus === 'countdown') {
    if (countdown > 0) {
timer = setTimeout(() => {
        setCountdown((prev: number) => {
          const newCountdown = prev - 1;
return newCountdown;
        });
      }, 1000);
    } else if (countdown === 0) {
finishCountdownAndStartExercises(); // ✅ APPEL DIRECT
    }
  }
  
  return () => {
    if (timer) {
      clearTimeout(timer);
    }
  };
}, [countdown, exerciseStatus, finishCountdownAndStartExercises]); // ✅ CORRECTION: Dépendance ajoutée

  // ===============================================
  // FONCTIONS DE NAVIGATION CORRIGÉES
  // ===============================================

  const goBackToDashboard = useCallback(() => {
 
  
    
    // Nettoyer l'état sauvé
    localStorage.removeItem(`exerciseMode_${languageCode}_state`);
  
    
    // Retour direct aux catégories
    onBackToCategories();
  }, [exerciseStatus, countdown, onBackToCategories, languageCode]);

  // ===============================================
  // FONCTIONS DE GESTION DES EXERCICES
  // ===============================================

  const checkAnswer = useCallback((answer: string) => {
    if (!currentExercise) return;
  
    let correct = false;
  
    if (currentExercise.type === 'association' && answer === "association-completed") {
      correct = true;
    } else {
      const cleanedAnswer = cleanParentheses(answer);
  
      if (Array.isArray(currentExercise.correctAnswer)) {
        correct = currentExercise.correctAnswer.some(ans => 
          compareWithTolerance(cleanedAnswer, cleanParentheses(ans))
        );
      } else {
        const correctStr = cleanParentheses(currentExercise.correctAnswer.toString());
        if (correctStr.includes(',')) {
          const parts = correctStr.split(',').map((p: string) => p.trim());
          correct = parts.some((part: string) => compareWithTolerance(cleanedAnswer, part));
        } else {
          correct = compareWithTolerance(cleanedAnswer, correctStr);
        }
      }
    }
  
    setIsCorrect(correct);
    setUserAnswer(answer);
  
    if (correct) {
      const newStreak = streak + 1;
      setScore((prev: number) => prev + 1);
      setStreak(newStreak);
    } else {
      setStreak(0);
    }
  
    setSessionUserAnswers(prev => {
      const updated = [...prev];
      updated[exerciseIndex] = answer;
      return updated;
    });
  
    setSessionCorrectAnswers(prev => {
      const updated = [...prev];
      updated[exerciseIndex] = correct;
      return updated;
    });

    // Afficher le feedback
    showFeedback(correct, correct ? t('quiz.resultExcellent') : t('exercise.feedback.wrong', { answer: Array.isArray(currentExercise.correctAnswer) ? currentExercise.correctAnswer[0] : currentExercise.correctAnswer }));
  
    if (currentExercise.data?.audio) {
      setTimeout(() => {
        playAudio(currentExercise.data?.audio || '').catch(() => {});
      }, 500);
    }
  
    setTimeout(() => {
      if (exerciseIndex < exercises.length - 1) {
        setExerciseIndex((prev: number) => prev + 1);
        const nextExercise = exercises[exerciseIndex + 1];
        setCurrentExercise(nextExercise);
        setUserAnswer('');
        setIsCorrect(null);
        setCompletedPairs([]);
        setPairsAssociations({});
        setSelectedLeftItem(null);
        setSelectedRightItem(null);

        if (nextExercise?.type === 'association' && nextExercise.pairs) {
          const leftItems = [...nextExercise.pairs].map(p => p.left);
          const rightItems = [...nextExercise.pairs].map(p => p.right);
          setShuffledLeftItems(shuffleArray(leftItems));
          setShuffledRightItems(shuffleArray(rightItems));
        }
      } else {
        const finalCorrectAnswers = [...sessionCorrectAnswers.slice(0, exerciseIndex), correct];
        const correctCount = finalCorrectAnswers.filter(Boolean).length;
        const totalCount = exercises.length;
        const duration = Date.now() - sessionStartTime;
        const finalScore = Math.round((correctCount / totalCount) * 100);

        const sessionResult: SessionResult = {
          correct: correctCount,
          total: totalCount,
          exercises: exercises,
          userAnswers: [...sessionUserAnswers.slice(0, exerciseIndex), answer],
          isCorrect: finalCorrectAnswers,
          sessionId: `session-${Date.now()}`,
          duration: duration,
          score: finalScore
        };

        setSessionResults(sessionResult);
        updateStats(sessionResult);
        setExerciseStatus('summary');
        
        setTimeout(() => {
          if (checkDailyLimit) {
            checkDailyLimit('exercisesPerDay');
          }
          onExerciseComplete();
        }, 2000);
      }
    }, 1500);
  }, [
    currentExercise,
    exerciseIndex,
    exercises,
    sessionUserAnswers,
    sessionCorrectAnswers,
    sessionStartTime,
    streak,
    playAudio,
    updateStats,
    onExerciseComplete,
    checkDailyLimit
  ]);

  // ===============================================
  // FONCTIONS D'INTERFACE
  // ===============================================

  const showFeedback = (isCorrect: boolean, message: string) => {
    const feedbackZone = document.querySelector('.ex-feedback-zone') as HTMLElement;
    const feedbackText = document.querySelector('.ex-feedback-zone .feedback-text') as HTMLElement;
    const feedbackIcon = document.querySelector('.ex-feedback-zone .feedback-icon') as HTMLElement;

    if (feedbackZone && feedbackText && feedbackIcon) {
      feedbackText.textContent = message;
      feedbackIcon.textContent = isCorrect ? '✓' : '↻';
      feedbackZone.className = `ex-feedback-zone ${isCorrect ? 'correct' : 'incorrect'} show`;

      setTimeout(() => {
        feedbackZone.className = 'ex-feedback-zone';
      }, 2500);
    }
  };

  

 

  // DIAGNOSTIC: Surveiller les remontages inattendus
  useEffect(() => {
    if (exerciseStatus === 'exercise' && exercises.length === 0) {
    }
  }, [exerciseStatus, exercises.length]);


// ===============================================
// GESTION PADDING - VERSION UNIQUE
// ===============================================
useEffect(() => {
// NE PAS TOUCHER AU BODY !
  // Laisser App.tsx gérer le padding
  
  return () => {
};
}, []);



  // ===============================================
  // RENDU DU DÉCOMPTE
  // ===============================================

  const renderCountdown = () => {
    return (
      <div className="immersive-dashboard-container">
        <div className="immersive-dashboard-icon"><Target size={48} strokeWidth={1.5} /></div>
        <h1 className="immersive-dashboard-title">{t('exercise.countdown.title', { lang: languageInfo.name })}</h1>
        <p className="immersive-dashboard-description" style={{ marginBottom: '3rem' }}>
          {t('exercise.countdown.description')}
        </p>
        
        <div 
          className="immersive-dashboard-icon countdown-icon"
          style={{
            fontSize: '8rem',
            color: countdown <= 1 ? 'var(--color-error)' : countdown <= 2 ? 'var(--gold-primary)' : 'var(--gold-light)',
            textShadow: '0 0 30px rgba(218, 165, 32, 0.8)',
            margin: '2rem 0'
          }}
        >
          {countdown || <Rocket size={64} strokeWidth={1.5} />}
        </div>
        
        <h2 className="immersive-dashboard-title" style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>
          {countdown > 0 ? t('exercise.countdown.startingIn', { count: countdown }) : t('exercise.countdown.goNow')}
        </h2>

        <p className="immersive-dashboard-description">
          {countdown > 0 ? t('exercise.countdown.getReady') : t('exercise.countdown.generating')}
        </p>
      </div>
    );
  };

  // ===============================================
  // RENDU DU COMPOSANT PRINCIPAL
  // ===============================================

  // 🔍 LOGS DE DEBUG
return (
    <div
      className={`exercise-immersive-container ${theme === 'dark' ? 'dark' : ''}`}
      data-show-back-button="true"
    >
      {/* Particules d'arrière-plan */}
      <div className="particles-container">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          />
        ))}
      </div>

     

      {/* Container principal */}

        <AnimatePresence>
          {/* Écran de décompte - masqué car countdown démarre à 0 */}
          {exerciseStatus === 'countdown' && countdown > 0 && (
            <motion.div
              key="countdown"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
            >
              {renderCountdown()}
            </motion.div>
          )}


        </AnimatePresence>

        {/* Exercice en cours */}
        {exerciseStatus === 'exercise' && currentExercise && (
          <div className="ex-screen">
            {/* Bandeau décoratif (Porte de Ségou pour ba / ff / wf) */}
            <TribalBorder />

            {/* Barre du haut */}
            <div className="ex-topbar">
              <button className="ex-back-btn" onClick={goBackToDashboard} title={t('exercise.back') as string}>
                <ArrowLeft size={18} />
              </button>
              <div className="ex-progress-wrap">
                <div className="ex-progress-segments">
                  {Array.from({ length: exercises.length }, (_, i) => (
                    <div
                      key={i}
                      className={`ex-seg${i < exerciseIndex ? ' done' : i === exerciseIndex ? ' current' : ''}`}
                    />
                  ))}
                </div>
                <span className="ex-progress-label">{exerciseIndex + 1}/{exercises.length}</span>
              </div>
              <div className="ex-score-pill">
                <span className="ex-heart"><Heart size={13} strokeWidth={2} fill="currentColor" /></span> {score}
              </div>
            </div>

            {/* Étiquette type d'exercice */}
            <div className="ex-type-row">
              {currentExercise.type === 'translation' ? (
                <>
                  <span className="ex-type-tab active">{t('exercise.types.openQuestion')}</span>
                  <span className="ex-type-sep">·</span>
                  <span className="ex-type-tab">{t('exercise.types.translation')}</span>
                </>
              ) : currentExercise.type === 'multipleChoice' ? (
                <span className="ex-type-tab active">{t('exercise.types.multipleChoice')}</span>
              ) : (
                <span className="ex-type-tab active">{t('exercise.types.association')}</span>
              )}
            </div>

            {/* Zone de question */}
            <div className="ex-question-zone">
              {(() => {
                // Greedy content + backtracking finds the LAST quote in the
                // string as the closing delimiter, so a word containing its
                // own apostrophe (e.g. English "Don't") doesn't prematurely
                // close the match on that internal apostrophe.
                const match = currentExercise.question.match(/^(.*?)['«](.+)['»](.*)$/s);
                if (match) {
                  return (
                    <div className="ex-question-parts">
                      <span className="ex-q-pre">{match[1].trim()}</span>
                      <span className="ex-q-word">« {match[2]} »</span>
                      <span className="ex-q-post">{match[3].trim()}</span>
                    </div>
                  );
                }
                return <h2 className="ex-q-plain">{currentExercise.question}</h2>;
              })()}
            </div>

            {/* Catégorie + Audio */}
            <div className="ex-pills-row">
              <div className="ex-cat-pill">
                <span className="ex-cat-icon"><Home size={13} strokeWidth={2} /></span>
                {(currentExercise.subcategory || currentExercise.category).toUpperCase()}
              </div>
              {currentExercise.data?.audio && currentExercise.question.toLowerCase().includes('en français') && (
                <button
                  className="ex-audio-pill"
                  onClick={() => playAudio(currentExercise.data?.audio || '').catch(() => {})}
                >
                  <Volume2 size={16} /> {t('learnMode.listenButton')}
                </button>
              )}
            </div>

            {/* Zone de réponse */}
            <div className="ex-answer-zone">
              {/* CHOIX MULTIPLES */}
              {currentExercise.type === 'multipleChoice' && currentExercise.options && (
                <div className="immersive-options-grid">
                  {currentExercise.options.map((option, index) => (
                    <button
                      key={index}
                      className={`immersive-option-button${
                        isCorrect === true && option === currentExercise.correctAnswer ? ' correct' : ''
                      }${isCorrect === false && userAnswer === option ? ' incorrect' : ''}`}
                      onClick={() => checkAnswer(option)}
                      disabled={userAnswer !== ''}
                    >
                      {cleanParentheses(option)}
                    </button>
                  ))}
                </div>
              )}

              {/* TRADUCTION */}
              {currentExercise.type === 'translation' && (
                <div className="ex-translation-zone">
                  <div className={`ex-input-box${isCorrect === true ? ' correct' : isCorrect === false ? ' incorrect' : ''}`}>
                    <input
                      type="text"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      placeholder={t('exercise.answerPlaceholder') as string}
                      className="ex-text-input"
                      disabled={isCorrect !== null}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userAnswer && isCorrect === null) {
                          checkAnswer(userAnswer);
                        }
                      }}
                    />
                  </div>
                  <button
                    className="ex-verify-btn"
                    onClick={() => checkAnswer(userAnswer)}
                    disabled={!userAnswer || isCorrect !== null}
                  >
                    {t('exercise.verify')}
                  </button>
                  <button
                    className="ex-skip-btn"
                    onClick={() => checkAnswer('')}
                    disabled={isCorrect !== null}
                  >
                    {t('exercise.dontKnow')}
                  </button>
                </div>
              )}

              {/* ASSOCIATION */}
              {currentExercise.type === 'association' && currentExercise.pairs && (
                <div className="immersive-association-container">
                  <div className="immersive-association-column">
                    <div className="immersive-association-header">{languageInfo.name}</div>
                    {shuffledLeftItems.map((item, index) => (
                      <button
                        key={`left-${index}`}
                        className={`immersive-association-item${selectedLeftItem === item ? ' selected' : ''}${completedPairs.includes(item) ? ' matched' : ''}`}
                        onClick={() => {
                          if (completedPairs.includes(item)) return;
                          setSelectedLeftItem(item);
                          if (selectedRightItem) {
                            const correctPair = currentExercise.pairs?.find(p => p.left === item && p.right === selectedRightItem);
                            if (correctPair) {
                              setCompletedPairs(prev => [...prev, item, selectedRightItem]);
                              setPairsAssociations(prev => ({ ...prev, [item]: selectedRightItem }));
                            }
                            setSelectedLeftItem(null);
                            setSelectedRightItem(null);
                            if (completedPairs.length + 2 === (currentExercise.pairs?.length || 0) * 2) {
                              setTimeout(() => checkAnswer("association-completed"), 500);
                            }
                          }
                        }}
                        disabled={completedPairs.includes(item)}
                      >
                        {cleanParentheses(item)}
                      </button>
                    ))}
                  </div>
                  <div className="immersive-connection-lines">
                    {Array.from({ length: Math.max(shuffledLeftItems.length, shuffledRightItems.length) }, (_, i) => (
                      <div key={i} style={{ fontSize: '1.2rem', color: 'rgba(139, 69, 19, 0.3)' }}>↔</div>
                    ))}
                  </div>
                  <div className="immersive-association-column">
                    <div className="immersive-association-header">{t('landing.vocabTable.colFrench')}</div>
                    {shuffledRightItems.map((item, index) => (
                      <button
                        key={`right-${index}`}
                        className={`immersive-association-item${selectedRightItem === item ? ' selected' : ''}${completedPairs.includes(item) ? ' matched' : ''}`}
                        onClick={() => {
                          if (completedPairs.includes(item)) return;
                          setSelectedRightItem(item);
                          if (selectedLeftItem) {
                            const correctPair = currentExercise.pairs?.find(p => p.left === selectedLeftItem && p.right === item);
                            if (correctPair) {
                              setCompletedPairs(prev => [...prev, selectedLeftItem, item]);
                              setPairsAssociations(prev => ({ ...prev, [selectedLeftItem]: item }));
                            }
                            setSelectedLeftItem(null);
                            setSelectedRightItem(null);
                            if (completedPairs.length + 2 === (currentExercise.pairs?.length || 0) * 2) {
                              setTimeout(() => checkAnswer("association-completed"), 500);
                            }
                          }
                        }}
                        disabled={completedPairs.includes(item)}
                      >
                        {cleanParentheses(item)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* Résumé de session */}
        {exerciseStatus === 'summary' && sessionResults && (() => {
          const mins = Math.floor(sessionResults.duration / 60000);
          const secs = String(Math.floor((sessionResults.duration % 60000) / 1000)).padStart(2, '0');
          const congratsText = sessionResults.score >= 80
            ? t('exercise.summary.great')
            : sessionResults.score >= 60
            ? t('exercise.summary.good')
            : t('exercise.summary.keepGoing');
          const correctItems = sessionResults.exercises.filter((_, i) => sessionResults.isCorrect[i]);
          const missedItems  = sessionResults.exercises.filter((_, i) => !sessionResults.isCorrect[i]);
          return (
            <SummaryScreen
              theme={theme}
              label={t('exercise.summary.label')}
              congrats={{ text: congratsText, translation: '' }}
              stats={[
                { value: `${sessionResults.score}%`,                        label: t('exercise.summary.score')    },
                { value: `${sessionResults.correct}/${sessionResults.total}`, label: t('exercise.summary.passed')  },
                { value: `${mins}:${secs}`,                                  label: t('exercise.summary.time')    },
              ]}
              primaryBtn={{
                label: t('exercise.summary.newSession'),
                onClick: () => {
                  setExerciseStatus('countdown');
                  setCountdown(0);
                  setIsCountdownActive(true);
                  setExercises([]);
                  setCurrentExercise(null);
                  setExerciseIndex(0);
                  setUserAnswer('');
                  setIsCorrect(null);
                  setScore(0);
                  setStreak(0);
                  setSessionResults(null);
                  setSessionUserAnswers([]);
                  setSessionCorrectAnswers([]);
                  setCompletedPairs([]);
                  setPairsAssociations({});
                  setSelectedLeftItem(null);
                  setSelectedRightItem(null);
                  setShuffledLeftItems([]);
                  setShuffledRightItems([]);
                },
              }}
              secondaryBtn={{ label: t('learnMode.summary.backToCategories'), onClick: goBackToDashboard }}
              onBack={goBackToDashboard}
              onShare={onShare}
            >
              {correctItems.length > 0 && (
                <div className="summ-word-section">
                  <p className="summ-word-section__title">{t('exercise.summary.successCount', { count: correctItems.length })}</p>
                  {correctItems.map((ex, i) => (
                    <div key={i} className="summ-word-row">
                      <span className="summ-word-row__word">{ex.wordKey || ex.correctAnswer as string}</span>
                      <span className="summ-word-row__badge summ-word-row__badge--ok">✓</span>
                    </div>
                  ))}
                </div>
              )}
              {missedItems.length > 0 && (
                <div className="summ-word-section">
                  <p className="summ-word-section__title">{t('learnMode.summary.toReviewCount', { count: missedItems.length })}</p>
                  {missedItems.map((ex, i) => (
                    <div key={i} className="summ-word-row">
                      <span className="summ-word-row__word">{ex.wordKey || ex.correctAnswer as string}</span>
                      <span className="summ-word-row__badge summ-word-row__badge--miss">↺</span>
                    </div>
                  ))}
                </div>
              )}
            </SummaryScreen>
          );
        })()}

     
    </div>
  );
};

export default ExerciseMode;